import React, { useState, useMemo } from 'react';
import { useEditor, useToolbar, useInspectorControl } from '@smartfire/js-block-engine';
import { RichText } from '@wordpress/block-editor';
import type { EditableTextWithAniNumProps } from './types';
import { generateFrontOfficeElement } from './helper-functions';
import { useEditableTextWithAniNumParams } from './hooks';
import { DEFAULT_PLACEHOLDER, MAX_ANIMATION_DURATION, MIN_ANIMATION_DURATION } from './constants';
import {
  ToolbarInstructions,
  ToolbarTagNameMenu,
  ToolbarTextAlignMenu,
  ToolbarFormats,
  ToolbarDecimalSeparatorForNumberDetectionMenu,
  ToolbarDetectedNumbers,
} from './wp-toolbar';
import {
  PanelAnimationDelayConfig,
  PanelColorSettings,
  PanelRenderedNumberFormatSettings,
  PanelSpringRelatedAnimationConfig,
} from './wp-panel';
import {
  getExtraStyles,
  validateAllowedDecimalSeparators,
  validateAllowedTagNames,
  validateAllowedThousandsSeparators,
} from './utils';

export const EditableTextWithAniNum = ({
  smartProp,
  placeholder = DEFAULT_PLACEHOLDER,
  allowedTagNames: unvalidatedAllowedTagNames,
  allowedDecimalSeparators: unvalidatedAllowedDecimalSeparators,
  allowedThousandsSeparators: unvalidatedAllowedThousandsSeparators,
  defaultAnimationDuration: unvalidatedDefaultAnimationDuration,

  // ComponentBasicProps
  tagProps,
  className,
  style = {},

  // AppliedToDetectedSubstrings
  classNamesForSubstrings,
  classNamesForSubstringsSkipDetectedNumbers,
  classNamesForDetectedNumbers,
  stylesForSubstrings,
  stylesForSubstringsSkipDetectedNumbers,
  stylesForDetectedNumbers,
}: EditableTextWithAniNumProps) => {
  const { isEditing } = useEditor();

  // ===== validate props =====

  const stringifiedUnvalidatedAllowedTagNames = JSON.stringify(unvalidatedAllowedTagNames ?? []);

  const stringifiedUnvalidatedAllowedDecimalSeparators = JSON.stringify(
    unvalidatedAllowedDecimalSeparators ?? [],
  );

  const stringifiedUnvalidatedAllowedThousandsSeparators = JSON.stringify(
    unvalidatedAllowedThousandsSeparators ?? [],
  );

  const allowedTagNames = useMemo(
    () => validateAllowedTagNames(stringifiedUnvalidatedAllowedTagNames),
    [stringifiedUnvalidatedAllowedTagNames],
  );

  const allowedDecimalSeparators = useMemo(
    () => validateAllowedDecimalSeparators(stringifiedUnvalidatedAllowedDecimalSeparators),
    [stringifiedUnvalidatedAllowedDecimalSeparators],
  );

  const allowedThousandsSeparators = useMemo(
    () => validateAllowedThousandsSeparators(stringifiedUnvalidatedAllowedThousandsSeparators),
    [stringifiedUnvalidatedAllowedThousandsSeparators],
  );

  // `undefined` or some value between (inclusive) [MIN|MAX]_ANIMATION_DURATION
  const defaultAnimationDuration =
    unvalidatedDefaultAnimationDuration === undefined
      ? undefined
      : Math.max(
          MIN_ANIMATION_DURATION,
          Math.min(MAX_ANIMATION_DURATION, unvalidatedDefaultAnimationDuration),
        );

  // ===== get values from the hook =====

  const {
    joinedDetectedNumberSubstrings,

    // raw string
    rawString,
    onChangeRawString,

    // tag name
    tagName,
    onClickTagName,

    // decimal separator for number detection
    decimalSeparatorForNumberDetection,
    onClickDecimalSeparatorForNumberDetection,

    // number format
    areDecimalPlacesFixed,
    decimalSeparator,
    thousandsSeparator,
    onChangeAreDecimalPlacesFixed,
    onChangeDecimalSeparator,
    onChangeThousandsSeparator,

    // spring related animation config
    animationDuration,
    springFriction,
    springTension,
    onChangeIsSpringBased,
    onChangeAnimationDuration,
    onChangeSpringFriction,
    onChangeSpringTension,

    // non spring related animation config
    defaultAnimationDelay,
    stringifiedLocalAnimationDelays,
    localIsAnimationDisabled,
    onChangeDefaultAnimationDelay,
    onChangeAnimationDelay,
    onClickResetAnimationDelay,
    onClickResetAllAnimationDelays,
    onClickIsAnimationDisabled,

    // extra styles
    isBold,
    isItalic,
    isUnderlined,
    textColor,
    backgroundColor,
    textAlign,
    onClickBold,
    onClickItalic,
    onClickUnderlined,
    onChangeTextColor,
    onChangeBackgroundColor,
    onClickTextAlign,
  } = useEditableTextWithAniNumParams({
    smartProp,
    allowedTagNames,
    allowedDecimalSeparators,
    allowedThousandsSeparators,
    defaultAnimationDuration,
  });

  // ===== wp toolbar =====

  useToolbar(
    () => (
      <>
        <ToolbarInstructions />
        <ToolbarTagNameMenu
          allowedTagNames={allowedTagNames}
          selectedTagName={tagName}
          onClick={onClickTagName}
        />
        <ToolbarTextAlignMenu selectedTextAlign={textAlign} onClick={onClickTextAlign} />
        <ToolbarFormats
          {...{
            isBold,
            isItalic,
            isUnderlined,
            onClickBold,
            onClickItalic,
            onClickUnderlined,
          }}
        />
        <ToolbarDecimalSeparatorForNumberDetectionMenu
          selectedValue={decimalSeparatorForNumberDetection}
          onClick={onClickDecimalSeparatorForNumberDetection}
        />
        <ToolbarDetectedNumbers
          {...{
            joinedDetectedSubstrings: joinedDetectedNumberSubstrings,
            localIsAnimationDisabled,
            onClickIsAnimationDisabled,
          }}
        />
      </>
    ),
    [
      allowedTagNames,
      tagName,
      onClickTagName,
      textAlign,
      onClickTextAlign,
      isBold,
      isItalic,
      isUnderlined,
      onClickBold,
      onClickItalic,
      onClickUnderlined,
      decimalSeparatorForNumberDetection,
      onClickDecimalSeparatorForNumberDetection,
      joinedDetectedNumberSubstrings,
      localIsAnimationDisabled,
      onClickIsAnimationDisabled,
    ],
  );

  // ===== wp panel =====

  const [isPanel1Open, setPanel1Open] = useState(false);
  const [isPanel2Open, setPanel2Open] = useState(false);
  const [isPanel3Open, setPanel3Open] = useState(false);
  const [isPanel4Open, setPanel4Open] = useState(false);

  useInspectorControl(
    () => (
      <>
        <PanelRenderedNumberFormatSettings
          {...{
            areDecimalPlacesFixed,
            decimalSeparator,
            thousandsSeparator,
            allowedDecimalSeparators,
            allowedThousandsSeparators,
            onChangeAreDecimalPlacesFixed,
            onChangeDecimalSeparator,
            onChangeThousandsSeparator,
            isPanelOpen: isPanel1Open,
            onTogglePanel: () => setPanel1Open((x) => !x),
          }}
        />
        <PanelSpringRelatedAnimationConfig
          {...{
            defaultAnimationDuration,
            animationDuration,
            springFriction,
            springTension,
            onChangeIsSpringBased,
            onChangeAnimationDuration,
            onChangeSpringFriction,
            onChangeSpringTension,
            isPanelOpen: isPanel2Open,
            onTogglePanel: () => setPanel2Open((x) => !x),
          }}
        />
        <PanelAnimationDelayConfig
          {...{
            joinedDetectedSubstrings: joinedDetectedNumberSubstrings,
            defaultAnimationDelay,
            stringifiedLocalAnimationDelays,
            localIsAnimationDisabled,
            onChangeDefaultAnimationDelay,
            onChangeAnimationDelay,
            onClickResetAnimationDelay,
            onClickResetAllAnimationDelays,
            isPanelOpen: isPanel3Open,
            onTogglePanel: () => setPanel3Open((x) => !x),
          }}
        />
        <PanelColorSettings
          {...{
            textColor,
            backgroundColor,
            onChangeTextColor,
            onChangeBackgroundColor,
            isPanelOpen: isPanel4Open,
            onTogglePanel: () => setPanel4Open((x) => !x),
          }}
        />
      </>
    ),
    [
      areDecimalPlacesFixed,
      decimalSeparator,
      thousandsSeparator,
      allowedDecimalSeparators,
      allowedThousandsSeparators,
      onChangeAreDecimalPlacesFixed,
      onChangeDecimalSeparator,
      onChangeThousandsSeparator,
      defaultAnimationDuration,
      animationDuration,
      springFriction,
      springTension,
      onChangeIsSpringBased,
      onChangeAnimationDuration,
      onChangeSpringFriction,
      onChangeSpringTension,
      joinedDetectedNumberSubstrings,
      defaultAnimationDelay,
      stringifiedLocalAnimationDelays,
      localIsAnimationDisabled,
      onChangeAnimationDelay,
      onChangeDefaultAnimationDelay,
      onClickResetAnimationDelay,
      onClickResetAllAnimationDelays,
      textColor,
      backgroundColor,
      onChangeTextColor,
      onChangeBackgroundColor,
      isPanel1Open,
      isPanel2Open,
      isPanel3Open,
      isPanel4Open,
    ],
  );

  // ===== return =====

  return isEditing ? (
    <RichText
      allowedFormats={[]}
      placeholder={placeholder}
      tagName={tagName}
      value={rawString}
      onChange={onChangeRawString}
      className={className}
      style={{
        ...(tagProps?.style ?? {}),
        ...style,
        ...getExtraStyles({
          isBold,
          isItalic,
          isUnderlined,
          textColor,
          backgroundColor,
          textAlign,
        }),
      }}
      // inlineToolbar // what is this
    />
  ) : (
    generateFrontOfficeElement({
      tagName,

      tagProps,
      className,
      style,

      isBold,
      isItalic,
      isUnderlined,
      textColor,
      backgroundColor,
      textAlign,

      rawString,
      decimalSeparatorForNumberDetection,

      areDecimalPlacesFixed,
      decimalSeparator,
      thousandsSeparator,

      animationDuration,
      springFriction,
      springTension,

      defaultAnimationDelay,
      stringifiedLocalAnimationDelays,
      localIsAnimationDisabled,

      classNamesForSubstrings,
      classNamesForSubstringsSkipDetectedNumbers,
      classNamesForDetectedNumbers,
      stylesForSubstrings,
      stylesForSubstringsSkipDetectedNumbers,
      stylesForDetectedNumbers,
    })
  );
};
